import type { EmailDeliverySummary as GQLItem } from '../../../generated/internal.graphql';
import * as EmailDeliverySummaryGroup from './EmailDeliverySummaryGroup';

/**
 * The grouped deliverability rollup: one row per group, plus the grand total across them.
 *
 * The schema names this envelope generically on purpose — it is the shape future native reports are
 * meant to reuse rather than an email-specific one — so the type stays just as literal about it.
 *
 * `total` counts every delivery the filter matched, but `groups` is capped server-side at the 1000
 * largest groups so a broad rollup stays inside the schema's cost budget. So the two do not have to
 * agree: summing `groups[].measures.sent` can come out below `total` on an unbounded all-accounts
 * read, and `total` is the one to trust for "how many deliveries".
 */
export type EmailDeliverySummary = {
  groups: EmailDeliverySummaryGroup.EmailDeliverySummaryGroup[];
  total: number;
};

/**
 * Both fields are non-null on the schema, so the fallbacks are not for today's resolver — they are
 * the same soft-failing posture the rest of this namespace takes, so a resolver that returns a
 * partial report yields an empty one rather than a `null` dereference, or an `undefined` rendered
 * into a total the caller can only report as an opaque failure. A wholly absent report is caught
 * one level up, in `Api`, where it resolves as a failure instead.
 */
export const fromGql = (item: GQLItem): EmailDeliverySummary => ({
  groups: (item.groups ?? []).map(EmailDeliverySummaryGroup.fromGql),
  total: item.total ?? 0,
});
